import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { teamApi } from '../api/team.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { Badge } from '../components/ui/Badge.jsx';
import { Copyable } from '../components/ui/Copyable.jsx';
import { Modal } from '../components/ui/Modal.jsx';
import { formatDate } from '../utils/dates.js';

function EditUserDialog({ open, onClose, user, onSaved }) {
  const [name, setName] = useState('');
  const [role, setRole] = useState('member');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open && user) {
      setName(user.name);
      setRole(user.role);
      setErr('');
    }
  }, [open, user]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      await teamApi.updateUser(user.id, { name, role });
      onSaved?.();
      onClose?.();
    } catch (ex) {
      if (ex.body?.error === 'last_admin_cannot_be_demoted') setErr('Nu se poate retrograda ultimul admin.');
      else setErr(ex.body?.error || 'Eroare');
    } finally {
      setBusy(false);
    }
  }

  if (!user) return null;

  return (
    <Modal open={open} onClose={onClose} title="Editează utilizator">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">Email</div>
          <div className="font-mono text-sm">{user.email}</div>
          <p className="mt-1 text-xs text-muted">Emailul nu poate fi modificat.</p>
        </div>

        {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted">Nume</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
          />
        </label>

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted">Rol</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5"
          >
            <option value="member">Utilizator</option>
            <option value="admin">Administrator</option>
          </select>
          <p className="mt-1 text-xs text-muted">
            Administratorii văd toate mașinile și pot gestiona alți utilizatori.
          </p>
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">Anulează</button>
          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? 'Se salvează…' : 'Salvează'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function InviteBox({ onInvited }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [lastUrl, setLastUrl] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function handle(e) {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const { url } = await teamApi.createInvite({ email, role });
      setLastUrl(url);
      setEmail('');
      onInvited?.();
    } catch (ex) {
      if (ex.body?.error === 'email_already_registered') setErr('Există deja un user cu acest email.');
      else setErr(ex.body?.error || 'Eroare');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-3">
      <h3 className="text-base font-semibold text-ink">Invită utilizator</h3>
      <p className="text-xs text-muted">
        Generează un link de activare. Userul va primi un cont cu care-și va gestiona mașinile.
      </p>
      {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}
      <form onSubmit={handle} className="space-y-2">
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <input
            type="email" required placeholder="email@exemplu.ro" value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
          />
          <select
            value={role} onChange={(e) => setRole(e.target.value)}
            className="rounded-lg border border-border bg-paper px-3 py-2.5"
          >
            <option value="member">Utilizator</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? 'Se generează…' : 'Generează link'}
        </button>
      </form>
      {lastUrl && (
        <div className="rounded-lg border border-coral/30 bg-coral-soft p-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-coral">Link — trimite-l persoanei</div>
          <Copyable value={lastUrl} as="button" className="mt-2 block w-full break-all rounded-lg border border-coral/30 bg-white px-3 py-2 text-left font-mono text-xs text-ink hover:border-coral">
            {lastUrl}
          </Copyable>
          <p className="mt-2 text-xs text-muted">Click pe link pentru a-l copia. Expiră în 7 zile.</p>
        </div>
      )}
    </div>
  );
}

function InvitesListBox({ refreshKey }) {
  const [invites, setInvites] = useState(null);

  async function load() {
    try {
      const { invites } = await teamApi.listInvites();
      setInvites(invites);
    } catch {
      setInvites([]);
    }
  }

  useEffect(() => { load(); }, [refreshKey]);

  async function revoke(id) {
    if (!confirm('Revoci invitația?')) return;
    try {
      await teamApi.revokeInvite(id);
      await load();
    } catch (ex) {
      alert(ex.body?.error || 'Eroare');
    }
  }

  if (!invites || invites.length === 0) return null;

  return (
    <div className="card space-y-2">
      <h3 className="text-base font-semibold text-ink">Invitații</h3>
      {invites.map((inv) => {
        const expired = !inv.accepted_at && new Date(inv.expires_at) < new Date();
        const status = inv.accepted_at ? 'accepted' : expired ? 'expired' : 'pending';
        return (
          <div key={inv.id} className="flex items-center justify-between gap-2 border-t border-border pt-2 first:border-0 first:pt-0">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 font-mono text-xs">
                <span className="truncate text-ink">{inv.email}</span>
                {inv.role === 'admin' && <Badge tone="warn">admin</Badge>}
              </div>
              <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted">
                {status === 'accepted' && <span className="text-ok">Acceptat {formatDate(inv.accepted_at)}</span>}
                {status === 'expired' && <span className="text-danger">Expirat {formatDate(inv.expires_at)}</span>}
                {status === 'pending' && <span>În așteptare · expiră {formatDate(inv.expires_at)}</span>}
              </div>
            </div>
            {status === 'pending' && (
              <button onClick={() => revoke(inv.id)} className="text-xs text-danger hover:underline">Revocă</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function UserRow({ user, currentUserId, onEdit, onDelete }) {
  const isSelf = user.id === currentUserId;
  return (
    <div className="card flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-ink">{user.name}</span>
          {user.role === 'admin' ? (
            <Badge tone="warn">Administrator</Badge>
          ) : (
            <Badge tone="muted">Utilizator</Badge>
          )}
          {isSelf && <Badge tone="ok">Tu</Badge>}
        </div>
        <Copyable value={user.email} as="button" className="mt-1 block font-mono text-xs text-muted hover:text-ink">
          {user.email}
        </Copyable>
        <div className="mt-1 text-[11px] text-muted">
          {user._count?.assigned_cars ?? 0} {(user._count?.assigned_cars ?? 0) === 1 ? 'mașină asignată' : 'mașini asignate'}
          {' · membru din '}{formatDate(user.created_at)}
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={() => onEdit(user)} className="btn-ghost border border-border text-xs">
          Editează
        </button>
        {!isSelf && (
          <button onClick={() => onDelete(user)} className="btn-ghost text-xs text-danger hover:bg-danger/10">
            Șterge
          </button>
        )}
      </div>
    </div>
  );
}

export function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [invitesRefresh, setInvitesRefresh] = useState(0);

  async function load() {
    try {
      const { users } = await teamApi.listUsers();
      setUsers(users);
    } catch (ex) {
      setErr(ex.body?.error || 'Eroare');
    }
  }

  useEffect(() => { load(); }, []);

  if (currentUser && currentUser.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  async function handleDelete(user) {
    if (!confirm(`Ștergi utilizatorul ${user.email}?\n\nMașinile asignate lui devin neasignate. Datele create de el (documente, închirieri) rămân.`)) return;
    try {
      await teamApi.deleteUser(user.id);
      await load();
    } catch (ex) {
      if (ex.body?.error === 'last_admin_cannot_be_deleted') alert('Nu se poate șterge ultimul admin.');
      else if (ex.body?.error === 'cannot_delete_self') alert('Nu te poți șterge singur.');
      else alert(ex.body?.error || 'Eroare');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-semibold tracking-tight text-ink">Utilizatori</h2>
        <Link to="/account" className="text-sm text-teal-bright underline hover:text-teal">
          ← Contul meu
        </Link>
      </div>

      {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

      <InviteBox onInvited={() => setInvitesRefresh((n) => n + 1)} />
      <InvitesListBox refreshKey={invitesRefresh} />

      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">
          {users ? `${users.length} ${users.length === 1 ? 'utilizator' : 'utilizatori'}` : ''}
        </h3>
        {users === null && <div className="card text-center text-muted">Se încarcă…</div>}
        {users && users.length === 0 && (
          <div className="card text-center text-sm text-muted">Niciun utilizator.</div>
        )}
        {users && (
          <div className="space-y-2">
            {users.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                currentUserId={currentUser.id}
                onEdit={setEditUser}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      <EditUserDialog
        open={Boolean(editUser)}
        user={editUser}
        onClose={() => setEditUser(null)}
        onSaved={load}
      />
    </div>
  );
}
